/**
 * Going live: leaving the demo, the checks the backend runs, and the request
 * that starts them.
 *
 * "Go live" on the demo creates the real workspace; setup then ends without
 * a button. The organisation asks LipaFlow to connect the
 * bank account payers pay into; once connected, the first payment the bank
 * reports into it completes setup on its own. `checkNow` only asks the
 * server to look again without waiting.
 */
import { api, getIdToken } from '@lipaflow/shared'

export interface GoLiveCheck {
  id: 'setup' | 'collection_account' | 'first_payment' | 'verification' | 'subscription' | string
  label: string
  done: boolean
  detail: string
}

export type AccountKind = 'bank_account' | 'paybill' | 'till'

export interface CollectionRequest {
  provider: string
  bank_name: string
  account_kind: AccountKind
  account_number: string
  account_name: string
  requested_at: string
}

export interface GoLiveState {
  is_live: boolean
  checks: GoLiveCheck[]
  collection_instructions?: { kind: 'paybill' | 'till' | 'bank_account'; number: string; reference: string | null; account_name: string | null; bank_name: string | null; ready: boolean } | null
  collection_request?: CollectionRequest | null
  connectable_banks?: { provider: string; name: string }[]
}

export async function getGoLiveState(): Promise<GoLiveState> {
  return (await api.get<GoLiveState>('/onboarding/progress/go-live-checks')).data
}

export async function checkNow(): Promise<{ status: 'live' | 'not_ready'; checks: GoLiveCheck[] }> {
  return (await api.post('/onboarding/progress/go-live')).data
}

export async function requestCollectionAccount(body: {
  provider: string
  account_kind: AccountKind
  account_number: string
  account_name: string
}): Promise<CollectionRequest> {
  return (await api.post('/onboarding/collection-account/request', body)).data.collection_request
}

export const ACCOUNT_KIND_LABELS: Record<AccountKind, string> = {
  bank_account: 'Bank account (via the bank paybill)',
  paybill: 'M-Pesa paybill',
  till: 'M-Pesa Till',
}

/**
 * "Go live" from the demo: the backend creates a fresh workspace, moves this
 * login to it and starts the trial. The current ID token still names the
 * demo, so it is refreshed before the portal reloads into setup.
 */
export async function goLiveFromDemo(): Promise<string> {
  const { data } = await api.post<{ next: string }>('/onboarding/demo/go-live')
  await getIdToken(true)
  return data.next
}
